<template>
  <div class="log-console" :class="{ 'is-wrapped': wrap }">
    <div v-if="loading && !entries.length" class="lc-state">
      <div class="lc-skeleton" v-for="n in 12" :key="n" :style="{ width: (35 + (n * 37) % 60) + '%' }"></div>
    </div>

    <div v-else-if="!entries.length" class="lc-state lc-empty">
      <i class="fa fa-file-text-o lc-empty-icon" aria-hidden="true"></i>
      <p class="mb-1">{{ emptyText }}</p>
      <small v-if="emptyHint">{{ emptyHint }}</small>
    </div>

    <!--
      Wrap off  -> RecycleScroller. Fixed row height, so the whole file can sit in the buffer.
      Wrap on   -> plain v-for. Row heights vary once lines wrap, which RecycleScroller cannot do,
                   so the buffer is capped instead (enforced by the parent).
    -->
    <RecycleScroller
      v-else-if="!wrap"
      ref="scroller"
      class="lc-body"
      :items="entries"
      :item-size="rowHeight"
      key-field="_key"
      role="log"
      aria-live="polite"
      aria-relevant="additions"
      @scroll.native="onScroll"
    >
      <template v-slot="{ item }">
        <div class="lc-row" :class="rowClass(item)" @click="toggle(item)">
          <span class="lc-gutter">{{ item._n }}</span>
          <span class="lc-time">{{ shortTime(item) }}</span>
          <span class="lc-level" :class="levelClass(item.level)" :title="item.level || 'no level'">{{ levelLetter(item.level) }}</span>
          <span class="lc-logger" :title="item.logger">{{ shortLogger(item.logger) }}</span>
          <span class="lc-msg" v-html="highlight(item.message)"></span>
          <span v-if="item.continuation.length" class="lc-fold">
            {{ isOpen(item) ? '▾' : '▸' }} {{ item.continuation.length }}
          </span>
        </div>
      </template>
    </RecycleScroller>

    <div v-else class="lc-body lc-body-plain" role="log" aria-live="polite" @scroll="onScroll" ref="plain">
      <div v-for="item in entries" :key="item._key">
        <div class="lc-row" :class="rowClass(item)" @click="toggle(item)">
          <span class="lc-gutter">{{ item._n }}</span>
          <span class="lc-time">{{ shortTime(item) }}</span>
          <span class="lc-level" :class="levelClass(item.level)">{{ levelLetter(item.level) }}</span>
          <span class="lc-logger" :title="item.logger">{{ shortLogger(item.logger) }}</span>
          <span class="lc-msg" v-html="highlight(item.message)"></span>
          <span v-if="item.continuation.length" class="lc-fold">
            {{ isOpen(item) ? '▾' : '▸' }} {{ item.continuation.length }}
          </span>
        </div>
        <pre v-if="isOpen(item)" class="lc-trace">{{ item.continuation.join('\n') }}</pre>
      </div>
    </div>

    <!-- Expanded traces render outside the virtualised list: variable height would break it. -->
    <div v-if="!wrap && openTrace" class="lc-trace-panel">
      <div class="lc-trace-head">
        <strong>{{ openTrace.continuation.length }} more lines</strong>
        <button type="button" class="lc-trace-close" @click="openKey = null" aria-label="Close trace">&times;</button>
      </div>
      <pre class="lc-trace">{{ openTrace.continuation.join('\n') }}</pre>
    </div>

    <button v-if="showJump" type="button" class="lc-jump" @click="$emit('jump-latest')">
      ↓ Jump to latest<span v-if="newCount"> ({{ newCount }} new)</span>
    </button>
  </div>
</template>

<script>
import { RecycleScroller } from 'vue-virtual-scroller'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'

/**
 * The log surface.
 *
 * A dark console inside the light CoreUI shell — deliberate: the panel stays a light Bootstrap
 * app, but a log viewer that isn't a terminal fights the reader's expectations, and this is where
 * the eyes sit for minutes at a time.
 *
 * Rendering notes:
 *   - RecycleScroller earns its place on scroll ANCHORING, not on the sizing maths. Follow mode
 *     appends below the viewport while "load older" inserts above it, and a hand-rolled scroller
 *     jumps the content under whoever is reading.
 *   - Level is never colour alone: every badge carries its letter, so it survives greyscale and
 *     colour-blindness (WCAG 1.4.1).
 *   - A stack trace is one row with a fold, not fourteen rows.
 */
export default {
  name: 'LogConsole',
  components: { RecycleScroller },
  props: {
    entries: { type: Array, default: function() { return [] } },
    loading: { type: Boolean, default: false },
    wrap: { type: Boolean, default: false },
    query: { type: String, default: '' },
    newCount: { type: Number, default: 0 },
    following: { type: Boolean, default: false },
    emptyText: { type: String, default: 'No log entries to show.' },
    emptyHint: { type: String, default: '' }
  },
  data: function() {
    return {
      rowHeight: 22,
      openKey: null,
      atBottom: true
    }
  },
  computed: {
    openTrace: function() {
      var self = this
      if (self.openKey === null) { return null }
      for (var i = 0; i < self.entries.length; i++) {
        if (self.entries[i]._key === self.openKey) { return self.entries[i] }
      }
      return null
    },
    // Only offered when the user has scrolled away, so following never yanks the viewport.
    showJump: function() {
      return !this.atBottom && this.entries.length > 0
    }
  },
  methods: {
    isOpen: function(item) { return this.openKey === item._key },
    toggle: function(item) {
      if (!item.continuation.length) { return }
      this.openKey = this.openKey === item._key ? null : item._key
    },
    rowClass: function(item) {
      return {
        'lc-partial': item.partial,
        'lc-has-trace': item.continuation.length > 0,
        'lc-open': this.isOpen(item)
      }
    },
    levelLetter: function(level) {
      if (!level) { return '·' }
      var l = level.toUpperCase()
      if (l === 'WARNING') { return 'W' }
      if (l === 'CRITICAL') { return 'C' }
      return l.charAt(0)
    },
    levelClass: function(level) {
      if (!level) { return 'lv-none' }
      var l = level.toUpperCase()
      if (l === 'ERROR' || l === 'FATAL' || l === 'CRITICAL') { return 'lv-e' }
      if (l === 'WARN' || l === 'WARNING') { return 'lv-w' }
      if (l === 'INFO') { return 'lv-i' }
      if (l === 'DEBUG') { return 'lv-d' }
      return 'lv-t'
    },
    shortTime: function(item) {
      if (!item.timestamp) { return '' }
      var m = String(item.timestamp).match(/(\d{2}:\d{2}:\d{2})/)
      return m ? m[1] : String(item.timestamp).slice(-12)
    },
    // Class names are long and the column is narrow; the full value is on the title attribute.
    shortLogger: function(logger) {
      if (!logger) { return '' }
      var parts = logger.split('.')
      return parts.length > 2 ? parts[parts.length - 1] : logger
    },
    escapeHtml: function(text) {
      return String(text === null || text === undefined ? '' : text)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    },
    escapeRegExp: function(text) {
      return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    },
    /** Escape first, then inject the mark — never the other way round. */
    highlight: function(message) {
      var safe = this.escapeHtml(message)
      var q = (this.query || '').trim()
      if (!q || q.length < 2) { return safe }
      try {
        return safe.replace(new RegExp(this.escapeRegExp(this.escapeHtml(q)), 'gi'), function(hit) {
          return '<mark class="lc-hit">' + hit + '</mark>'
        })
      } catch (badPattern) {
        return safe
      }
    },
    onScroll: function(event) {
      var el = event.target
      if (!el) { return }
      var distance = el.scrollHeight - el.scrollTop - el.clientHeight
      var wasAtBottom = this.atBottom
      this.atBottom = distance < 40
      if (wasAtBottom !== this.atBottom) {
        this.$emit('at-bottom-change', this.atBottom)
      }
    },
    scrollToBottom: function() {
      var self = this
      self.$nextTick(function() {
        var scroller = self.$refs.scroller
        var el = scroller ? scroller.$el : self.$refs.plain
        if (el) {
          el.scrollTop = el.scrollHeight
          self.atBottom = true
        }
      })
    },
    scrollToTop: function() {
      var scroller = this.$refs.scroller
      var el = scroller ? scroller.$el : this.$refs.plain
      if (el) { el.scrollTop = 0 }
    }
  }
}
</script>

<style scoped>
/* Console palette. Fixed dark surface by design — this pane is a terminal. */
.log-console {
  position: relative;
  background: #0b1215;
  border: 1px solid #22323a;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 320px;
}

.lc-body {
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: auto;
  padding: 6px 0;
  font-family: ui-monospace, "SF Mono", SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace;
  font-size: 12px;
  line-height: 22px;
  color: #d6e3e8;
}
.lc-body-plain { line-height: 1.6; }

.lc-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 0 12px;
  height: 22px;
  white-space: pre;
  cursor: default;
}
.is-wrapped .lc-row { height: auto; white-space: normal; align-items: baseline; }
.lc-row:hover { background: rgba(127, 192, 238, 0.06); }
.lc-has-trace { cursor: pointer; }
.lc-open { background: rgba(127, 192, 238, 0.1); }

.lc-gutter {
  flex: 0 0 52px;
  text-align: right;
  color: #55686f;
  font-variant-numeric: tabular-nums;
  user-select: none;
}
.lc-time {
  flex: 0 0 62px;
  color: #7d939b;
  font-variant-numeric: tabular-nums;
}
.lc-level {
  flex: 0 0 18px;
  text-align: center;
  font-weight: 700;
  border-radius: 2px;
}
.lc-logger { flex: 0 0 168px; color: #7d939b; overflow: hidden; text-overflow: ellipsis; }
.lc-msg { flex: 1 1 auto; color: #d6e3e8; }
.is-wrapped .lc-msg { word-break: break-word; }
.lc-fold { flex: 0 0 auto; color: #f0b657; font-size: 11px; }

/* Colour AND letter — never colour alone. */
.lv-e { color: #ff8a7a; background: rgba(255, 138, 122, 0.16); }
.lv-w { color: #f0b657; background: rgba(240, 182, 87, 0.16); }
.lv-i { color: #7fc0ee; background: rgba(127, 192, 238, 0.12); }
.lv-d { color: #8ca0a7; background: rgba(140, 160, 167, 0.1); }
.lv-t { color: #6f868e; }
.lv-none { color: #4c5f66; }

.lc-partial .lc-msg { color: #9fb0b6; font-style: italic; }

.lc-trace-panel {
  border-top: 1px solid #22323a;
  background: #0f181c;
  max-height: 40%;
  overflow: auto;
}
.lc-trace-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 12px;
  color: #f0b657;
  font-size: 11px;
  font-family: ui-monospace, Menlo, Consolas, monospace;
  border-bottom: 1px solid #22323a;
}
.lc-trace-close {
  background: none;
  border: 0;
  color: #7d939b;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
}
.lc-trace-close:hover { color: #d6e3e8; }
.lc-trace {
  margin: 0;
  padding: 8px 12px 12px 66px;
  color: #a9bcc3;
  background: transparent;
  border: 0;
  font-family: ui-monospace, Menlo, Consolas, monospace;
  font-size: 11.5px;
  line-height: 1.6;
  white-space: pre;
  overflow-x: auto;
}

.lc-jump {
  position: absolute;
  right: 16px;
  bottom: 14px;
  background: #106e8c;
  color: #fff;
  border: 0;
  border-radius: 14px;
  padding: 5px 14px;
  font-size: 12px;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.45);
}
.lc-jump:hover { background: #14839f; }
.lc-jump:focus-visible { outline: 2px solid #7fc0ee; outline-offset: 2px; }

.lc-state {
  flex: 1 1 auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.lc-empty { align-items: center; text-align: center; color: #7d939b; }
.lc-empty-icon { font-size: 26px; margin-bottom: 10px; opacity: 0.5; }

/* Skeleton rather than a bare spinner: the shape of what is coming. */
.lc-skeleton {
  height: 10px;
  border-radius: 2px;
  background: linear-gradient(90deg, #16232a 0%, #1e2f37 50%, #16232a 100%);
  background-size: 200% 100%;
  animation: lc-shimmer 1.3s ease-in-out infinite;
  margin-bottom: 10px;
}
@keyframes lc-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
@media (prefers-reduced-motion: reduce) {
  .lc-skeleton { animation: none; }
}
</style>

<style>
/* Unscoped: the highlight lives inside a v-html payload. */
.log-console .lc-hit {
  background: #f0b657;
  color: #101a1e;
  border-radius: 2px;
  padding: 0 1px;
}
.log-console .vue-recycle-scroller__item-wrapper { overflow: visible; }
</style>
